<script setup lang="ts">
import type { SlugNews } from '@@/server/types/common'

type Props = {
  news: SlugNews
  isLight?: boolean
  isFull?: boolean
  withImage?: boolean
}

const props = defineProps<Props>()

const newsCardClasses = computed(() => {
  return {
    'news-card--light': props.isLight,
    'news-card--full': props.isFull,
  }
})

const iShowImage = computed(() => Boolean(props.withImage && props.news.image))
</script>

<template>
  <div class="news-card" :class="newsCardClasses" data-test-id="news-card">
    <nuxt-img
      v-if="iShowImage"
      class="news-card__image"
      :src="news.image"
      :alt="news.title"
      data-test-id="news-image"
    />

    <div class="news-card__date" data-test-id="news-date">{{ news.date }}</div>

    <div class="news-card__title" data-test-id="news-title">
      {{ news.title }}
    </div>

    <div
      v-if="isFull"
      class="news-card__content"
      data-test-id="news-content"
      v-html="news.content"
    ></div>
    <div v-else class="news-card__description" data-test-id="news-description">
      {{ news.short_text }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.news-card {
  width: 282px;
  height: max-content;
  padding: 20px;
  color: var(--ui-text-primary);
  overflow-wrap: break-word;
  background-color: var(--ui-color-white);
  border-radius: 20px;

  &__image {
    width: 100%;
    height: 147px;
    margin-bottom: 24px;
    object-fit: cover;
    border-radius: 20px;
  }

  &__date {
    font-size: 12px;
    font-weight: 400;
    color: var(--ui-text-primary);
  }

  &__title {
    margin-top: 15px;
    font-size: 16px;
    font-weight: 800;
    line-height: 19px;
  }

  &__description {
    margin-top: 5px;
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
  }

  @include media('<desktop-mini') {
    &__image {
      height: 128px;
    }
  }

  @include media('<tablet') {
    width: 100%;
    max-width: unset;

    &__image {
      height: 131px;
    }
  }

  &--light {
    box-shadow: 0 6px 30px rgba(50, 52, 72, 0.1);

    .news-card__date {
      font-weight: 600;
    }

    .news-card__title {
      margin-top: 10px;
      font-size: 16px;
      font-weight: 800;
      line-height: 19px;
    }

    .news-card__description {
      margin-top: 20px;
      font-weight: 500;
    }
  }

  &--full {
    width: 100%;
    max-width: 788px;
    padding: 40px;
    background-color: var(--ui-color-white);
    box-shadow: none;

    .news-card__image {
      height: 374px;
      margin-bottom: 40px;
      border-radius: 40px;
    }

    .news-card__date {
      font-size: 14px;
      font-weight: 600;
    }

    .news-card__title {
      margin-top: 10px;
      font-size: 22px;
      font-weight: 800;
      line-height: 19px;
    }

    .news-card__content {
      margin-top: 30px;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;

      :deep(a) {
        font-weight: 600;
      }
    }

    @include media('<desktop-mini') {
      max-width: unset;

      .news-card__image {
        height: 242px;
      }
    }

    @include media('<tablet') {
      padding: 20px;

      .news-card__image {
        height: 131px;
        border-radius: 20px;
      }

      .news-card__date {
        font-size: 12px;
      }

      .news-card__title {
        font-size: 16px;
      }

      .news-card__content {
        margin-top: 20px;
      }
    }
  }

  &--full.news-card--light {
    max-width: 955px;
    padding: 0;
    background-color: inherit;

    @include media('<desktop-mini') {
      max-width: unset;
    }
  }
}
</style>
